import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/api/client';
import type { TemplateKpiRow } from '@/api/types';
import { formatPct } from '@/features/campaigns/campaignUi';
import campStyles from '@/features/campaigns/Campaigns.module.css';

type Props = {
  projectId: string;
  /** Highlight row + scroll focus in catalog via parent */
  highlightTemplateId?: string | null;
  compact?: boolean;
};

export function aggregateTemplateKpi(rows: TemplateKpiRow[]) {
  const map = new Map<string, { sent: number; replies: number }>();
  for (const r of rows) {
    const cur = map.get(r.email_template_id) ?? { sent: 0, replies: 0 };
    cur.sent += r.sent;
    cur.replies += r.replies;
    map.set(r.email_template_id, cur);
  }
  const out = new Map<string, { sent: number; replies: number; reply_rate: number | null }>();
  for (const [id, v] of map) {
    out.set(id, {
      ...v,
      reply_rate: v.sent > 0 ? v.replies / v.sent : null,
    });
  }
  return out;
}

export function EmailTemplatePerformancePanel({
  projectId,
  highlightTemplateId,
  compact,
}: Props) {
  const kpiQ = useQuery({
    queryKey: ['email-template-kpi', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ templateKpi: TemplateKpiRow[] }>(
        `/api/email-templates/kpi?project_id=${encodeURIComponent(projectId)}`,
      ),
  });

  const rows = kpiQ.data?.templateKpi ?? [];
  if (kpiQ.isLoading) {
    return (
      <section className={campStyles.panel}>
        <div className={campStyles.panelHead}>
          <h2 className={campStyles.panelTitle}>Template prestaties</h2>
        </div>
        <p className={campStyles.panelBodyMuted}>Statistieken laden…</p>
      </section>
    );
  }
  if (kpiQ.isError) return null;
  if (!rows.length) {
    if (compact) return null;
    return (
      <section className={campStyles.panel}>
        <div className={campStyles.panelHead}>
          <h2 className={campStyles.panelTitle}>Template prestaties</h2>
        </div>
        <p className={campStyles.panelBodyMuted}>
          Nog geen verzendingen met template-attributie. Sends via campagnes loggen{' '}
          <code className={campStyles.mutedInline}>email_template_id</code> automatisch.
        </p>
      </section>
    );
  }

  const totalSent = rows.reduce((a, r) => a + r.sent, 0);

  return (
    <section className={campStyles.panel}>
      <div className={campStyles.panelHead}>
        <div>
          <h2 className={campStyles.panelTitle}>Template prestaties</h2>
          <p className={campStyles.panelBodyMuted} style={{ margin: '0.25rem 0 0' }}>
            Projectbreed · <strong>{totalSent}</strong> sends met template-id
          </p>
        </div>
      </div>
      <div className={campStyles.panelBody}>
        <div className={campStyles.tableWrap}>
          <table className={campStyles.dataTable}>
            <thead>
              <tr>
                <th>Template</th>
                <th>Ver.</th>
                <th>Verstuurd</th>
                <th>Reply</th>
                <th>CI</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const highlighted = highlightTemplateId === t.email_template_id;
                return (
                  <tr
                    key={`${t.email_template_id}-${t.template_version}`}
                    className={highlighted ? campStyles.tableRowHighlight : undefined}
                  >
                    <td>
                      <Link
                        to={`/app/campagnes/bibliotheek?template=${t.email_template_id}`}
                        className={campStyles.catalogLink}
                      >
                        {t.template_name}
                      </Link>
                    </td>
                    <td>v{t.template_version}</td>
                    <td>{t.sent}</td>
                    <td>{formatPct(t.reply_rate, 2)}</td>
                    <td>
                      {t.reply_ci
                        ? `${formatPct(t.reply_ci.low, 1)} – ${formatPct(t.reply_ci.high, 1)}`
                        : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
